// SimForge Baseball Game Configuration
// Easy config for game economics and fees

// ============ LEAGUE CONFIG ============

export interface LeagueSet {
  id: string;
  name: string;
  description?: string;
  startDate: string;    // ISO date
  endDate: string;      // ISO date
  isActive: boolean;
}

export interface League {
  id: string;
  name: string;
  tier: 1 | 2 | 3 | 4 | 5;  // 1=Elite, 2=Platinum, 3=Gold, 4=Silver, 5=Bronze
  seasonId?: string;
  status: 'upcoming' | 'active' | 'completed' | 'paused';
  startDate?: string;
  endDate?: string;
  leagueSetId?: string;
  isAiRun?: boolean;  // Developmental leagues always AI-run
  createdAt: string;
  updatedAt: string;
}

export const TIER_NAMES: Record<number, string> = {
  1: 'Premier',
  2: 'Major', 
  3: 'AAA',
  4: 'AA',
  5: 'Rookie'
};

export const TIER_COLORS: Record<number, string> = {
  1: '#b9f2ff',
  2: '#e5e4e2',
  3: '#ffd700',
  4: '#c0c0c0',
  5: '#cd7f32'
};

// Default league sets (one for now, can add more later)
export const DEFAULT_LEAGUE_SET: LeagueSet = {
  id: 'default_set',
  name: 'Season 1',
  description: 'Default season - all leagues run together',
  startDate: '2026-04-01',
  endDate: '2026-09-30',
  isActive: true
};

// Simulation config
export interface SimulationConfig {
  isRunning: boolean;
  currentDay: number;
  speed: 'slow' | 'normal' | 'fast' | 'instant';
  gamesPerDay: number;  // How many games to sim per "day"
}

export const DEFAULT_SIMULATION_CONFIG: SimulationConfig = {
  isRunning: false,
  currentDay: 0,
  speed: 'normal',
  gamesPerDay: 10  // How many games per simulated day
};

// ============ CONTRACT SETTINGS ============

export interface ContractSettings {
  maxYears: number;       // Max contract length (default 5)
  minYears: number;       // Min contract length (default 1)
  maxSalary: number;      // Max salary per year in BALLS
  minSalary: number;      // Min salary per year in BALLS
  maxBonus: number;       // Max signing bonus in BALLS
  maxTotalValue: number;  // Max total contract value in BALLS
}

export const DEFAULT_CONTRACT_SETTINGS: Record<number, ContractSettings> = {
  1: { maxYears: 5, minYears: 1, maxSalary: 50000, minSalary: 1000, maxBonus: 10000, maxTotalValue: 200000 },  // Elite
  2: { maxYears: 5, minYears: 1, maxSalary: 30000, minSalary: 800, maxBonus: 7500, maxTotalValue: 150000 },   // Platinum
  3: { maxYears: 4, minYears: 1, maxSalary: 15000, minSalary: 500, maxBonus: 5000, maxTotalValue: 75000 },    // Gold
  4: { maxYears: 3, minYears: 1, maxSalary: 8000, minSalary: 300, maxBonus: 2500, maxTotalValue: 35000 },    // Silver
  5: { maxYears: 2, minYears: 1, maxSalary: 4000, minSalary: 200, maxBonus: 1000, maxTotalValue: 15000 },     // Bronze
};

// ============ FORGE CONFIG ============

export interface ForgeConfig {
  // Breeding fees
  breedingFeeBase: number;        // Base fee (2000 BALLS)
  breedingFeeListingMin: number;  // Minimum listing fee to owner (1000 BALLS)
  
  // Fee distribution (must sum to 1.0)
  feeDistribution: {
    listingOwner: number;    // % to player owner
    leaguePot: number;       // % to league prize pool
    devFund: number;         // % to developers
  };
  
  // Limits
  maxBreedsPerPlayerPerSeason: number;       // 5
  maxNewPlayersReceivedPerSeason: number;    // 1
  totalForgeSeasonsAfterRetirement: number; // 10 seasons, then Hall of Fame
  
  // Commissioners (emails that can access admin)
  commissionerEmails: string[];
}

export const FORGE_CONFIG: ForgeConfig = {
  breedingFeeBase: 2000,
  breedingFeeListingMin: 1000,
  
  feeDistribution: {
    listingOwner: 0.33,   // 33% to owner
    leaguePot: 0.34,     // 34% to league
    devFund: 0.33,       // 33% to devs
  },
  
  maxBreedsPerPlayerPerSeason: 5,
  maxNewPlayersReceivedPerSeason: 1,
  totalForgeSeasonsAfterRetirement: 10,
  
  commissionerEmails: ['gerardan@ymail.com', 'qctest@simforgebaseball.com'],
};

// Helper to calculate fees
export function calculateBreedingFees(listingPrice: number): {
  ownerPayout: number;
  leaguePayout: number;
  devPayout: number;
  total: number;
} {
  const { breedingFeeBase, breedingFeeListingMin, feeDistribution } = FORGE_CONFIG;
  
  const effectiveListingFee = Math.max(listingPrice, breedingFeeListingMin);
  
  return {
    ownerPayout: effectiveListingFee * feeDistribution.listingOwner,
    leaguePayout: effectiveListingFee * feeDistribution.leaguePot,
    devPayout: effectiveListingFee * feeDistribution.devFund,
    total: effectiveListingFee + breedingFeeBase,
  };
}

// Check if user is commissioner
export function isCommissioner(email: string): boolean {
  return FORGE_CONFIG.commissionerEmails.includes(email.toLowerCase());
}
